import { useAuthStore } from '../stores/auth.store';
import { IS_MOBILE } from '../lib/platform';
import { secureStorage } from '../lib/secureStorage';

/**
 * API base URL strategy:
 *   - If VITE_API_BASE_URL is set at build time, use it verbatim. This is the
 *     escape hatch for staging / preview deploys.
 *   - Otherwise default by target:
 *       native (iOS/Android via Capacitor): absolute prod URL (no Vite proxy
 *         exists on device, requests must hit the public origin).
 *       web (dev or PWA): relative `/api/v1` so Vite proxy / nginx reverse
 *         proxy keep working.
 */
const ENV_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
const API_BASE_URL =
  ENV_BASE_URL && ENV_BASE_URL.length > 0
    ? ENV_BASE_URL
    : IS_MOBILE
    ? 'https://solid-cycle.mrtacojr.com/api/v1'
    : '/api/v1';

const REFRESH_TOKEN_KEY = 'sc.refreshToken';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class ApiService {
  private baseUrl: string;
  /**
   * C16: in-flight singleton promise for /auth/refresh. While this promise is
   * non-null, every concurrent caller of `refreshToken()` reuses the same
   * promise instead of issuing a parallel POST. Backend rotates refresh tokens
   * single-use (apps/api/src/modules/auth/auth.service.ts:237-292), so without
   * this mutex N concurrent 401s produce 1 winner + N-1 `TOKEN_REVOKED`
   * losers — and each loser triggers a forced logout via the 401 interceptor.
   *
   * The promise resolves with `true` on success and `false` on any failure.
   * The `finally` clear is mandatory: if it leaks, future refreshes deadlock.
   */
  private _refreshInFlight: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      // C15: dropped `credentials: 'include'`. apps/api still authenticates via
      // Bearer token (refresh in body). httpOnly cookie pair-migration is
      // deferred; including credentials would force CORS preflight against
      // capacitor:// schemes for no benefit.
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - try to refresh token (C16: mutex-guarded, see refreshToken).
        if (response.status === 401 && endpoint !== '/auth/refresh') {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Retry original request
            return this.request<T>(endpoint, options);
          } else {
            // Logout user
            await persistRefreshToken(null);
            useAuthStore.getState().logout();
          }
        }

        return {
          success: false,
          error: data.error || {
            code: 'UNKNOWN_ERROR',
            message: 'Ha ocurrido un error inesperado',
          },
        };
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Error de conexión. Verifica tu internet.',
        },
      };
    }
  }

  /**
   * C16: refresh the access token using the cached refresh token, with a
   * global mutex so N concurrent 401s only fire 1 POST /auth/refresh.
   *
   * - If a refresh is already in flight, return that same promise.
   * - Otherwise, kick off a new refresh and store its promise in
   *   `_refreshInFlight`. The `finally` clear runs whether the inner promise
   *   resolved or rejected, so the next refresh cycle is unblocked.
   *
   * The wrapper is idempotent: calling it 10 times within the same tick still
   * results in exactly 1 network request.
   */
  refreshToken(): Promise<boolean> {
    if (this._refreshInFlight) {
      return this._refreshInFlight;
    }
    this._refreshInFlight = this._doRefresh().finally(() => {
      this._refreshInFlight = null;
    });
    return this._refreshInFlight;
  }

  /**
   * Inner implementation of the refresh flow. Never call directly — always go
   * through `refreshToken()` so the mutex is honored.
   */
  private async _doRefresh(): Promise<boolean> {
    try {
      // Prefer the in-memory cache (already hydrated at boot); fall back to
      // secureStorage if memory was cleared (e.g. tab restored).
      let currentRefreshToken = useAuthStore.getState().refreshToken;
      if (!currentRefreshToken) {
        currentRefreshToken = await secureStorage.getItem(REFRESH_TOKEN_KEY);
        if (currentRefreshToken) {
          useAuthStore.getState().setRefreshToken(currentRefreshToken);
        }
      }
      if (!currentRefreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.success && data.data?.tokens) {
        const { accessToken, refreshToken: newRefreshToken } = data.data.tokens;
        const store = useAuthStore.getState();
        store.setAccessToken(accessToken);
        // Token rotation — persist the new refresh token to secureStorage.
        if (newRefreshToken) {
          store.setRefreshToken(newRefreshToken);
          await persistRefreshToken(newRefreshToken);
        }
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // C29 PR-2: `body` OPCIONAL. Cambio estrictamente aditivo — los ~decenas de
  // callers existentes llaman `delete(endpoint)` y siguen enviando un DELETE sin
  // cuerpo, byte por byte igual que antes. Lo necesita el Clean Slate, cuya
  // confirmación (el nombre del hogar) NO puede viajar en la URL: acabaría en los
  // access logs de nginx, que es exactamente lo que SEC-003 prohíbe.
  async delete<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }
}

/**
 * Persist a refresh token to secureStorage. Pass `null` to clear it.
 * Exported so auth.service.ts (login/logout/register) can keep the canonical
 * store in sync without leaking secureStorage details into other modules.
 */
export async function persistRefreshToken(token: string | null): Promise<void> {
  if (token === null || token === undefined) {
    await secureStorage.removeItem(REFRESH_TOKEN_KEY);
  } else {
    await secureStorage.setItem(REFRESH_TOKEN_KEY, token);
  }
}

/**
 * Hydrate the in-memory refresh token from secureStorage. Called once at
 * startup (src/lib/mobileBootstrap.ts) before React renders, so the very
 * first 401 → refresh flow has the token available without a roundtrip.
 */
export async function hydrateRefreshTokenFromStorage(): Promise<string | null> {
  const stored = await secureStorage.getItem(REFRESH_TOKEN_KEY);
  if (stored) {
    useAuthStore.getState().setRefreshToken(stored);
  }
  return stored;
}

export const API_BASE_URL_RESOLVED = API_BASE_URL;
export const api = new ApiService(API_BASE_URL);

/**
 * C16: pre-React refresh-first bootstrap. Called from mobileBootstrap before
 * ReactDOM.createRoot().render() so that — by the time any component mounts
 * — the in-memory accessToken is either valid or definitively gone.
 *
 * Decision Option C (T1-A handoff §5):
 *   - accessToken is NEVER persisted to disk (web localStorage or native
 *     Keychain) — only refreshToken lives in secureStorage.
 *   - Cold start: read refreshToken from secureStorage → POST /auth/refresh →
 *     populate accessToken in memory. If that fails, clear stale tokens and
 *     let the router redirect to /login.
 *   - This single round-trip replaces the previous "10 parallel 401s on first
 *     mount → 9× TOKEN_REVOKED → forced logout" race that bit Capacitor cold
 *     starts deterministically.
 *
 * Result type: `'authenticated'` if the access token is now in memory,
 * `'unauthenticated'` if no session could be re-established.
 *
 * Reuses ApiService.refreshToken() so it inherits the same mutex — if a
 * runtime refresh races against bootstrap, only one POST is fired.
 */
export async function bootstrapAuth(): Promise<'authenticated' | 'unauthenticated'> {
  try {
    const stored = await secureStorage.getItem(REFRESH_TOKEN_KEY);
    if (!stored) {
      // No persisted session; nothing to refresh. Caller will route to /login
      // or stay on a public page depending on existing auth state.
      return 'unauthenticated';
    }
    // Mirror into the in-memory store so refreshToken() does not need another
    // secureStorage round-trip.
    useAuthStore.getState().setRefreshToken(stored);

    const ok = await api.refreshToken();
    if (ok) {
      return 'authenticated';
    }

    // Refresh failed (token expired, revoked, network down). Clear stale
    // state so the next cold start starts from a clean slate, and the router
    // will redirect to /login on the first protected route hit.
    await persistRefreshToken(null);
    useAuthStore.getState().logout();
    return 'unauthenticated';
  } catch {
    // Defensive: never reject — boot must always resolve so React can render.
    try {
      await persistRefreshToken(null);
    } catch {
      // best-effort
    }
    useAuthStore.getState().logout();
    return 'unauthenticated';
  }
}
